/**
 * Development-only Mock Persona definitions and helpers.
 *
 * Provides a richer, human-readable Persona selection UX for local testing
 * while cleanly mapping to the existing Dev_smartfix auth contract:
 * { role, jobType, sectionId }.
 *
 * Zero mockKey/personaKey architecture, zero DB dependencies.
 * Every persona now carries a verified development affiliationKey.
 */

export const isMockAuthEnabled = (mockAuthFlag) => {
  if (mockAuthFlag === 'true') return true;
  if (mockAuthFlag === 'false') return false;
  return process.env.VUE_APP_AUTH_BYPASS === 'true' || process.env.NODE_ENV !== 'production';
};

export const MOCK_PERSONAS = Object.freeze([
  // Reporters / General Users
  {
    id: "user-01",
    role: "user",
    personClass: "staff",
    profile: { name: "ณิชา", surname: "พรหมรักษา" },
    description: "Reporter / Staff",
    affiliationKey: "130000"
  },
  {
    id: "user-02",
    role: "user",
    personClass: "staff",
    profile: { name: "กฤติน", surname: "วัฒนกุล" },
    description: "Reporter / Faculty",
    affiliationKey: "200000"
  },
  {
    id: "user-03",
    role: "user",
    personClass: "student",
    profile: { name: "พิมพ์ชนก", surname: "ศรีสวัสดิ์" },
    description: "Reporter / Student",
    affiliationKey: "401000"
  },
  {
    id: "user-04",
    role: "user",
    personClass: "student",
    profile: { name: "กานต์", surname: "พิชญเดช" },
    description: "Reporter / Student (School of Applied Digital Technology)",
    affiliationKey: "130000"
  },

  // Student Affairs
  {
    id: "student-affairs-01",
    role: "student_affairs",
    personClass: "staff",
    profile: { name: "นภาลัย", surname: "สุขเกษม" },
    description: "Student Affairs Officer / ส่วนพัฒนานักศึกษา",
    affiliationKey: "413000"
  },

  // Technicians (mapped to exact active SmartFix maintenance jobTypes)
  {
    id: "tech-cas-av-01",
    role: "technician",
    personClass: "staff",
    profile: { name: "ธนกฤต", surname: "แสงสุวรรณ" },
    jobType: "โสตทัศนูปกรณ์และห้องประชุม",
    sectionCode: "CAS",
    departmentCode: "AV-CLASS",
    affiliationKey: "620000"
  },
  {
    id: "tech-cas-av-02",
    role: "technician",
    personClass: "staff",
    profile: { name: "ชนาธิป", surname: "อินทร์แก้ว" },
    jobType: "อุปกรณ์ห้องเรียนและการสอนออนไลน์",
    sectionCode: "CAS",
    departmentCode: "AV-CLASS",
    affiliationKey: "620000"
  },
  {
    id: "tech-cdti-itsup-01",
    role: "technician",
    personClass: "staff",
    profile: { name: "ปกรณ์", surname: "ธรรมวงศ์" },
    jobType: "คอมพิวเตอร์และอุปกรณ์",
    sectionCode: "CDTI",
    departmentCode: "IT-SUP",
    affiliationKey: "600000"
  },
  {
    id: "tech-cdti-itsup-02",
    role: "technician",
    personClass: "staff",
    profile: { name: "อธิวัฒน์", surname: "บุญช่วย" },
    jobType: "เครื่องพิมพ์และสแกนเนอร์",
    sectionCode: "CDTI",
    departmentCode: "IT-SUP",
    affiliationKey: "600000"
  },
  {
    id: "tech-cdti-itsup-03",
    role: "technician",
    personClass: "staff",
    profile: { name: "ศุภณัฐ", surname: "คำมูล" },
    jobType: "บัญชีผู้ใช้ อีเมล และ Google Workspace",
    sectionCode: "CDTI",
    departmentCode: "IT-SUP",
    affiliationKey: "600000"
  },
  {
    id: "tech-cdti-itnet-01",
    role: "technician",
    personClass: "staff",
    profile: { name: "นราวิชญ์", surname: "จันทร์เพ็ญ" },
    jobType: "อินเทอร์เน็ตและเครือข่าย",
    sectionCode: "CDTI",
    departmentCode: "IT-NET",
    affiliationKey: "600000"
  },
  {
    id: "tech-cdti-itnet-02",
    role: "technician",
    personClass: "staff",
    profile: { name: "วรเมธ", surname: "สุขเกษม" },
    jobType: "กล้องวงจรปิดและระบบสื่อสาร",
    sectionCode: "CDTI",
    departmentCode: "IT-NET",
    affiliationKey: "600000"
  },
  {
    id: "tech-cdti-itdev-01",
    role: "technician",
    personClass: "staff",
    profile: { name: "ภูริณัฐ", surname: "ตั้งเจริญ" },
    jobType: "ระบบสารสนเทศและซอฟต์แวร์",
    sectionCode: "CDTI",
    departmentCode: "IT-DEV",
    affiliationKey: "600000"
  },
  {
    id: "tech-building-engmep-01",
    role: "technician",
    personClass: "staff",
    profile: { name: "กิตติพงษ์", surname: "มณีวงศ์" },
    jobType: "ไฟฟ้าและแสงสว่าง",
    sectionCode: "BUILDING",
    departmentCode: "ENG-MEP",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-engmep-02",
    role: "technician",
    personClass: "staff",
    profile: { name: "ณัฐดนัย", surname: "ใจดี" },
    jobType: "ประปาและสุขาภิบาล",
    sectionCode: "BUILDING",
    departmentCode: "ENG-MEP",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-engmep-03",
    role: "technician",
    personClass: "staff",
    profile: { name: "พีรพัฒน์", surname: "วงศ์คำ" },
    jobType: "เครื่องปรับอากาศและระบบเครื่องกล",
    sectionCode: "BUILDING",
    departmentCode: "ENG-MEP",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-engmep-04",
    role: "technician",
    personClass: "staff",
    profile: { name: "ธีรภัทร", surname: "แก้วมา" },
    jobType: "ลิฟต์และประตูอัตโนมัติ",
    sectionCode: "BUILDING",
    departmentCode: "ENG-MEP",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-engmep-05",
    role: "technician",
    personClass: "staff",
    profile: { name: "เอกภพ", surname: "ศรีชัย" },
    jobType: "โทรศัพท์ภายใน",
    sectionCode: "BUILDING",
    departmentCode: "ENG-MEP",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-civil-01",
    role: "technician",
    personClass: "staff",
    profile: { name: "ศรัณย์", surname: "ชูศักดิ์" },
    jobType: "อาคารและงานโยธา",
    sectionCode: "BUILDING",
    departmentCode: "CIVIL",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-civil-02",
    role: "technician",
    personClass: "staff",
    profile: { name: "อัครเดช", surname: "พูนผล" },
    jobType: "เฟอร์นิเจอร์และครุภัณฑ์ทั่วไป",
    sectionCode: "BUILDING",
    departmentCode: "CIVIL",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-envsafe-01",
    role: "technician",
    personClass: "staff",
    profile: { name: "รัชพล", surname: "ธนากร" },
    jobType: "พื้นที่ภายนอกและภูมิทัศน์",
    sectionCode: "BUILDING",
    departmentCode: "ENV-SAFE",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-envsafe-02",
    role: "technician",
    personClass: "staff",
    profile: { name: "กานต์พิชชา", surname: "วิเศษวงศ์" },
    jobType: "ความสะอาดและสิ่งแวดล้อม",
    sectionCode: "BUILDING",
    departmentCode: "ENV-SAFE",
    affiliationKey: "407000"
  },
  {
    id: "tech-building-envsafe-03",
    role: "technician",
    personClass: "staff",
    profile: { name: "สิรวิชญ์", surname: "ปัญญาดี" },
    jobType: "ความปลอดภัยและอัคคีภัย",
    sectionCode: "BUILDING",
    departmentCode: "ENV-SAFE",
    affiliationKey: "407000"
  },
  {
    id: "tech-asset-rent-01",
    loginEmail: "kasem.bok@mfu.ac.th",
    role: "technician",
    personClass: "staff",
    profile: { name: "เกษม", surname: "บ่อแก้ว" },
    jobType: "ซ่อมบำรุงระบบไฟฟ้า",
    sectionCode: "ASSET",
    departmentCode: "ASSET-RENT",
    affiliationKey: "409000"
  },
  {
    id: "tech-asset-accom-01",
    loginEmail: "chotiwat.asset@mfu.ac.th",
    role: "technician",
    personClass: "staff",
    profile: { name: "โชติวัต", surname: "กิจรักษ์" },
    jobType: "ซ่อมบำรุงระบบปรับอากาศ,ระบบลิฟท์,เครื่องทำน้ำเย็น",
    sectionCode: "ASSET",
    departmentCode: "ASSET-ACCOM",
    affiliationKey: "409000"
  },
  {
    id: "tech-asset-food-01",
    loginEmail: "sanit.asset@mfu.ac.th",
    role: "technician",
    personClass: "staff",
    profile: { name: "สนิท", surname: "ปินตาสาร" },
    jobType: "ซ่อมบำรุงระบบประปา,ระบบสุขาภิบาล",
    sectionCode: "ASSET",
    departmentCode: "ASSET-FOOD",
    affiliationKey: "409000"
  },

  // Supervisors
  {
    id: "sup-cas-01",
    role: "supervisor",
    personClass: "staff",
    profile: { name: "พชรพล", surname: "รัตนานนท์" },
    sectionCode: "CAS",
    sectionName: "ศูนย์บริการวิชาการ",
    affiliationKey: "620000"
  },
  {
    id: "sup-cdti-01",
    role: "supervisor",
    personClass: "staff",
    profile: { name: "เมธาวี", surname: "กุลวงศ์" },
    sectionCode: "CDTI",
    sectionName: "ศูนย์เทคโนโลยีดิจิทัลและสารสนเทศ",
    affiliationKey: "600000"
  },
  {
    id: "sup-ton",
    role: "supervisor",
    personClass: "staff",
    profile: { name: "Mock", surname: "Ton" },
    sectionCode: "CDTI",
    sectionName: "ศูนย์เทคโนโลยีดิจิทัลและสารสนเทศ",
    affiliationKey: "600000"
  },
  {
    id: "sup-building-01",
    role: "supervisor",
    personClass: "staff",
    profile: { name: "จิรายุส", surname: "ตั้งมั่น" },
    sectionCode: "BUILDING",
    sectionName: "ส่วนอาคารสถานที่",
    affiliationKey: "407000"
  },
  {
    id: "sup-asset-01",
    loginEmail: "supatra.ari@mfu.ac.th",
    role: "supervisor",
    personClass: "staff",
    profile: { name: "สุพัตรา", surname: "อริยะมั่ง" },
    sectionCode: "ASSET",
    sectionName: "สำนักงานจัดการทรัพย์สินและรายได้",
    affiliationKey: "409000"
  },
  {
    id: "sup-asset-02",
    loginEmail: "natchanon.phu@mfu.ac.th",
    role: "supervisor",
    personClass: "staff",
    profile: { name: "ณัฐชนน", surname: "ภูวินท์" },
    sectionCode: "ASSET",
    sectionName: "สำนักงานจัดการทรัพย์สินและรายได้",
    affiliationKey: "409000"
  },

  // Administrators
  {
    id: "admin-01",
    role: "admin",
    personClass: "staff",
    profile: { name: "อรอุมา", surname: "วิริยะกุล" },
    description: "System Administrator",
    affiliationKey: "600000"
  }
]);

export const findMockPersonaById = (personaId) => {
  if (!personaId || typeof personaId !== "string") return null;
  return MOCK_PERSONAS.find(p => p.id === personaId.trim()) || null;
};

export const getPersonaEmail = (persona) => {
  if (!persona || !persona.id) return null;
  return persona.loginEmail || `mock_${persona.id.replace(/-/g, "_")}@mfu.ac.th`;
};

export const filterMockPersonasByRole = (personas, role) =>
  (Array.isArray(personas) ? personas : []).filter(persona => persona.role === role);

export const formatMockPersonaOption = (persona) => {
  if (!persona || !persona.profile) return "";
  const fullName = `${persona.profile.name} ${persona.profile.surname}`;

  if (persona.role === 'technician') {
    const parts = [persona.sectionCode, persona.departmentCode, persona.jobType].filter(Boolean);
    return `${fullName} — ${parts.join(' / ')}`;
  }
  if (persona.role === 'supervisor') {
    return `${fullName} — ${persona.sectionName || persona.sectionCode || 'Supervisor'}`;
  }
  if (persona.role === 'user') {
    return persona.affiliationKey
      ? `${fullName} — Affiliation ${persona.affiliationKey}`
      : `${fullName} — ${persona.description || 'Reporter'}`;
  }
  if (persona.role === 'student_affairs') {
    return `${fullName} — ส่วนพัฒนานักศึกษา (Student Affairs)`;
  }
  if (persona.role === 'admin') {
    return `${fullName} — ผู้ดูแลระบบ (Admin)`;
  }
  return fullName;
};

export const formatPersonaLabel = formatMockPersonaOption;

/**
 * Maps a selected UI persona to the canonical Dev_smartfix mock login contract:
 * { role, jobType?, sectionId? }
 *
 * Absolutely NO mockKey or personaKey is emitted.
 */
export const mapPersonaToDevAuthPayload = (persona, availableSections = []) => {
  if (!persona) return null;

  const payload = {
    role: persona.role,
    jobType: null,
    sectionId: null
  };

  if (persona.role === 'technician') {
    payload.jobType = persona.jobType || null;
  } else if (persona.role === 'supervisor') {
    const sections = Array.isArray(availableSections) ? availableSections : [];
    const matched = sections.find(s =>
      (persona.sectionCode && (s.code === persona.sectionCode || s.displayName?.includes(persona.sectionCode))) ||
      (persona.sectionName && s.displayName?.includes(persona.sectionName)) ||
      s._id === persona.sectionId
    );
    payload.sectionId = matched ? matched._id : (sections[0]?._id || null);
  }

  return payload;
};
